import { createSlice } from '@reduxjs/toolkit';
import type { Invoices, InvoiceDetails } from './models/invoice.model';
import invoiceService from '../services/invoiceService';
import type { AppDispatch } from './store';
import { TripLegAttestation, TripLegID } from './models/trip-leg.model';
import * as providerSlice from './provider.slice';

interface InvoiceState {
  invoices: Invoices;
  loadingInvoices: boolean;
  loadedInvoices: boolean;
  invoiceError: string;
  invoiceDetails: InvoiceDetails | null;
  providerInvoiceCreatedAt: string | null;
}

export const initialState: InvoiceState = {
  invoices: {
    count: 0,
    limit: 0,
    offset: 0,
    invoices: [],
  },
  loadingInvoices: false,
  loadedInvoices: false,
  invoiceError: '',
  invoiceDetails: null,
  providerInvoiceCreatedAt: null,
};

export const slice = createSlice({
  name: 'invoice',
  initialState,
  reducers: {
    setInvoices: (state: InvoiceState, action: { payload: Invoices }) => {
      state.invoices = action.payload;
    },
    setLoadingInvoices: (state: InvoiceState, action: { payload: boolean }) => {
      state.loadingInvoices = action.payload;
    },
    setLoadedInvoices: (state: InvoiceState, action: { payload: boolean }) => {
      state.loadedInvoices = action.payload;
    },
    setInvoiceError: (state: InvoiceState, action: { payload: string }) => {
      state.invoiceError = action.payload;
    },
    setInvoiceDetails: (
      state: InvoiceState,
      action: { payload: InvoiceDetails },
    ) => {
      state.invoiceDetails = action.payload;
    },
    setProviderInvoiceCreatedAt: (
      state: InvoiceState,
      action: { payload: string },
    ) => {
      state.providerInvoiceCreatedAt = action.payload;
    },
  },
});

export const {
  setInvoices,
  setLoadingInvoices,
  setLoadedInvoices,
  setInvoiceError,
  setInvoiceDetails,
  setProviderInvoiceCreatedAt,
} = slice.actions;

export const fetchInvoicesAsync =
  (providerID: number) => (dispatch: AppDispatch) => {
    dispatch(setLoadingInvoices(true));
    dispatch(setLoadedInvoices(false));

    invoiceService.getInvoices(providerID).subscribe({
      next: (invoices) => {
        dispatch(setInvoices(invoices));
        dispatch(setLoadingInvoices(false));
        dispatch(setLoadedInvoices(true));
      },
      error: () => {
        dispatch(setLoadingInvoices(false));
        dispatch(setLoadedInvoices(false));
      },
    });
  };

export const fetchTripLegsByProviderInvoiceIDAsync =
  (providerInvoiceID: number) => (dispatch: AppDispatch) => {
    invoiceService.getInvoiceDetails(providerInvoiceID).subscribe({
      next: (invoiceDetails) => {
        dispatch(setInvoiceDetails(invoiceDetails));
      },
    });
  };
export const selectInvoices = (state: { invoice: InvoiceState }) =>
  state.invoice.invoices;
export const selectInvoiceDetails = (state: { invoice: InvoiceState }) =>
  state.invoice.invoiceDetails;
export const selectLoadingInvoices = (state: { invoice: InvoiceState }) =>
  state.invoice.loadingInvoices;
export const selectLoadedInvoices = (state: { invoice: InvoiceState }) =>
  state.invoice.loadedInvoices;
export const selectInvoiceError = (state: { invoice: InvoiceState }) =>
  state.invoice.invoiceError;
export const selectProviderInvoiceCreatedAt = (state: {
  invoice: InvoiceState;
}) => state.invoice.providerInvoiceCreatedAt;
export default slice.reducer;
